import type { Dict } from '../index.js';

/** Arabic: productivity. Keys are the exact English text. */
export const productivity: Dict = {
  // Shared words used on the productivity screens (duplicates of common are harmless: common wins)
  'Period': 'الفترة',
  'From': 'من',
  'To': 'إلى',
  'Employee': 'الموظف',
  'Date': 'التاريخ',
  'Day': 'اليوم',
  'Days': 'الأيام',
  'Time': 'الوقت',
  'People': 'الأشخاص',
  'Name': 'الاسم',
  'Type': 'النوع',
  'Text': 'النص',
  'Add': 'إضافة',
  'Cancel': 'إلغاء',
  'Save': 'حفظ',
  'Remove': 'إزالة',
  'Done': 'تم',
  'Copy': 'نسخ',
  'Print': 'طباعة',
  'Never': 'أبدًا',
  'Everyone': 'الجميع',
  'Dashboard': 'لوحة المعلومات',
  'Worked': 'ساعات العمل',
  ', ': '، ',
  'Edit {name}': 'تعديل {name}',
  'Delete {name}': 'حذف {name}',
  'Late {duration}': 'متأخر {duration}',

  // Classes and figures
  'Productivity': 'الإنتاجية',
  'Productive': 'منتج',
  'Unproductive': 'غير منتج',
  'Neutral': 'محايد',
  'Not classified': 'غير مصنَّف',
  'Idle': 'خامل',
  'Tracked': 'مسجَّل',
  'Score': 'النتيجة',
  'Score {pct}%': 'النتيجة {pct}%',
  'Score {pct}% · frozen': 'النتيجة {pct}% · مجمَّدة',
  'Target {pct}%': 'الهدف {pct}%',
  'Total': 'الإجمالي',
  '{n}h': '{n} س',
  'No activity': 'لا يوجد نشاط',
  'Only idle time.': 'وقت خمول فقط.',
  'From – to': 'من – إلى',

  // Productivity page and tabs
  'Daily report': 'التقرير اليومي',
  'By employee': 'حسب الموظف',
  'Apps & websites': 'التطبيقات والمواقع',
  'Computers': 'أجهزة الكمبيوتر',
  'From clock-in to clock-out: time in productive and unproductive apps and websites, and idle time. No keystrokes are recorded.':
    'من تسجيل الحضور إلى تسجيل الانصراف: الوقت في التطبيقات والمواقع المنتجة وغير المنتجة، ووقت الخمول. لا تُسجَّل ضغطات المفاتيح.',
  'Choose an employee…': 'اختر موظفًا…',
  'Choose an employee': 'اختر موظفًا',

  // Not classified tab
  'Used in the last': 'المستخدمة خلال آخر',
  '{n} days': '{n} يومًا',
  'Classifying an item applies to all days, including past ones (closed reporting periods keep their frozen figures).':
    'يسري تصنيف العنصر على جميع الأيام، بما فيها الأيام السابقة (تحتفظ الفترات المقفلة بأرقامها المجمَّدة).',
  'App / website': 'التطبيق / الموقع',
  'Classify as': 'تصنيف كـ',
  'website': 'موقع',
  'app': 'تطبيق',
  'Everything is classified': 'كل شيء مصنَّف',
  'New apps and websites appear here as the engineers use them.': 'تظهر هنا التطبيقات والمواقع الجديدة عندما يستخدمها المهندسون.',

  // Apps & websites (rules) tab
  'Search… e.g. revit': 'بحث… مثلًا revit',
  'Search rules': 'البحث في القواعد',
  'Load starter list': 'تحميل القائمة المبدئية',
  '{n} apps and websites added from the starter list.': 'تمت إضافة {n} من التطبيقات والمواقع من القائمة المبدئية.',
  'The starter list is already loaded.': 'القائمة المبدئية محمَّلة بالفعل.',
  'Matches': 'يطابق',
  'Class': 'التصنيف',
  'Applies to': 'ينطبق على',
  'website {pattern}': 'موقع {pattern}',
  'title contains {pattern}': 'العنوان يحتوي على {pattern}',
  'app {pattern}': 'تطبيق {pattern}',
  'Only {name}': '{name} فقط',
  '{name} only': '{name} فقط',
  'No match': 'لا توجد نتائج مطابقة',
  'No apps or websites classified yet': 'لم يتم تصنيف أي تطبيقات أو مواقع بعد',
  'Load the starter list for an engineering office, then adjust it — or add your own.':
    'حمِّل القائمة المبدئية لمكتب هندسي ثم عدِّلها، أو أضف عناصرك الخاصة.',
  'Remove this rule?': 'إزالة هذه القاعدة؟',
  '{name} will count as “not classified” again (past days too, except closed periods).':
    'سيُحتسب {name} مرة أخرى «غير مصنَّف» (بما في ذلك الأيام السابقة، باستثناء الفترات المقفلة).',
  'Edit app or website': 'تعديل تطبيق أو موقع',
  'Add app or website': 'إضافة تطبيق أو موقع',
  'Application (program file, e.g. revit.exe)': 'تطبيق (ملف البرنامج، مثلًا revit.exe)',
  'Website (e.g. youtube.com — includes its subdomains)': 'موقع (مثلًا youtube.com — يشمل نطاقاته الفرعية)',
  'Window title contains… (only if titles are recorded)': 'عنوان النافذة يحتوي على… (فقط إذا كانت العناوين مسجَّلة)',
  'Website': 'الموقع',
  'Program file': 'ملف البرنامج',
  'invoice': 'فاتورة',
  'Display name (optional)': 'الاسم المعروض (اختياري)',
  'Counts as': 'يُحتسب كـ',
  'A department': 'قسم',
  'One person (exception)': 'شخص واحد (استثناء)',
  'A personal exception wins over the department and company rules for this person only — e.g. YouTube counts as productive for someone who watches Revit tutorials.':
    'يتقدَّم الاستثناء الشخصي على قواعد القسم والشركة لهذا الشخص فقط — مثلًا يُحتسب YouTube منتجًا لمن يشاهد دروس Revit.',
  'Category (optional)': 'الفئة (اختياري)',
  'Engineering & design': 'الهندسة والتصميم',
  'Note (optional)': 'ملاحظة (اختياري)',

  // Computers tab
  'The installer has not been placed on the server yet (folder {folder}).': 'لم يتم وضع ملف التثبيت على الخادم بعد (المجلد {folder}).',
  '1. Install the desktop agent (Windows)': '1. تثبيت برنامج سطح المكتب (Windows)',
  'Download installer': 'تنزيل ملف التثبيت',
  'Browser extension (optional)': 'إضافة المتصفح (اختيارية)',
  "Install it on the employee's laptop — no administrator rights needed. It starts with Windows and shows an icon next to the clock. The optional Chrome/Edge extension gives exact website names; without it common sites (YouTube, Facebook, Gmail…) are still recognised.":
    'ثبِّته على الكمبيوتر المحمول للموظف — لا يحتاج إلى صلاحيات مسؤول. يعمل مع بدء تشغيل Windows ويُظهر أيقونة بجانب الساعة. توفِّر إضافة Chrome/Edge الاختيارية أسماء المواقع بدقة؛ وبدونها يتم التعرُّف على المواقع الشائعة (YouTube وFacebook وGmail…) أيضًا.',
  '2. Connect the computer': '2. ربط الكمبيوتر',
  'Choose…': 'اختر…',
  'Get connection code': 'الحصول على رمز الربط',
  'On first start the agent asks for the server address and this one-time code. The employee then sees the monitoring notice and must accept it before anything is recorded.':
    'عند التشغيل الأول يطلب البرنامج عنوان الخادم وهذا الرمز الصالح لمرة واحدة. بعد ذلك يرى الموظف إشعار المراقبة ويجب أن يوافق عليه قبل تسجيل أي شيء.',
  'Connected computers': 'أجهزة الكمبيوتر المرتبطة',
  'Computer': 'الكمبيوتر',
  'Connected': 'تاريخ الربط',
  'Last contact': 'آخر اتصال',
  'agent {version}': 'البرنامج {version}',
  'Disconnected': 'مفصول',
  'Disconnect': 'فصل',
  'No computers connected yet': 'لا توجد أجهزة كمبيوتر مرتبطة بعد',
  'Connection code for {name}': 'رمز الربط لـ {name}',
  'Works once, until {time}.': 'صالح لمرة واحدة حتى {time}.',
  'Server address:': 'عنوان الخادم:',
  'Disconnect this computer?': 'فصل هذا الكمبيوتر؟',
  '{name} stops sending data immediately. Its past data is kept.': 'سيتوقف {name} عن إرسال البيانات فورًا، مع الاحتفاظ ببياناته السابقة.',

  // Dashboard
  "Install the desktop agent on each engineer's laptop (Computers tab).": 'ثبِّت برنامج سطح المكتب على الكمبيوتر المحمول لكل مهندس (تبويب أجهزة الكمبيوتر).',
  'Team score': 'نتيجة الفريق',
  '{n} of {total} people': '{n} من {total} أشخاص',
  'Classify in “Not classified”': 'صنِّفها في «غير مصنَّف»',
  'Hours per day': 'الساعات يوميًا',
  'Team hours per day by class': 'ساعات الفريق يوميًا حسب التصنيف',
  'Score {pct}% · {n} person · click for the daily report': 'النتيجة {pct}% · شخص واحد · انقر لعرض التقرير اليومي',
  'Score {pct}% · {n} people · click for the daily report': 'النتيجة {pct}% · {n} أشخاص · انقر لعرض التقرير اليومي',
  'No activity recorded': 'لم يُسجَّل أي نشاط',
  'Score by person': 'النتيجة حسب الشخص',
  '{time} tracked over {n} day(s)': '{time} مسجَّلة خلال {n} يوم/أيام',
  'Nothing recorded in this period yet.': 'لم يُسجَّل شيء في هذه الفترة بعد.',
  'Time of day': 'وقت اليوم',
  'Team activity by hour of day': 'نشاط الفريق حسب ساعة اليوم',
  '{from} – {to} (all days, everyone)': '{from} – {to} (كل الأيام، الجميع)',
  'Most used apps & websites': 'التطبيقات والمواقع الأكثر استخدامًا',
  'Nothing yet.': 'لا شيء بعد.',
  'Website · used by {n} person': 'موقع · يستخدمه شخص واحد',
  'Website · used by {n} people': 'موقع · يستخدمه {n} أشخاص',
  'App · used by {n} person': 'تطبيق · يستخدمه شخص واحد',
  'App · used by {n} people': 'تطبيق · يستخدمه {n} أشخاص',
  'Hours per day by class': 'الساعات يوميًا حسب التصنيف',

  // Daily report
  'Previous day': 'اليوم السابق',
  'Next day': 'اليوم التالي',
  'Daily productivity report — {date}': 'تقرير الإنتاجية اليومي — {date}',
  'Nobody to report on': 'لا يوجد أحد في التقرير',
  'People appear here when they clock in or have a computer connected.': 'يظهر الأشخاص هنا عند تسجيل حضورهم أو عند ربط جهاز كمبيوتر لهم.',
  'People with activity': 'أشخاص لديهم نشاط',
  'of {n} listed': 'من أصل {n}',
  'This day is in a closed reporting period — figures are frozen.': 'هذا اليوم ضمن فترة مقفلة — الأرقام مجمَّدة.',
  'Clock in – out': 'الحضور – الانصراف',
  'Computer activity': 'نشاط الكمبيوتر',
  'From computer — no clocking in': 'من الكمبيوتر — بدون تسجيل حضور',
  'No computer connected': 'لا يوجد كمبيوتر مرتبط',
  'Clock in – out comes from attendance (fingerprint, door QR, app or agent). Computer activity is what the desktop agent recorded while recording was allowed. Score = productive ÷ tracked time (including idle).':
    'يأتي الحضور – الانصراف من سجل الحضور (البصمة، رمز QR على الباب، التطبيق أو البرنامج). نشاط الكمبيوتر هو ما سجَّله برنامج سطح المكتب أثناء السماح بالتسجيل. النتيجة = الوقت المنتج ÷ الوقت المسجَّل (بما فيه الخمول).',
  'First activity {first} · last {last} · neutral {neutral} · not classified {unknown}':
    'أول نشاط {first} · آخر نشاط {last} · محايد {neutral} · غير مصنَّف {unknown}',

  // My productivity
  'Recorded only while you are clocked in': 'يُسجَّل فقط أثناء تسجيل حضورك',
  'Recorded only during your scheduled working hours': 'يُسجَّل فقط خلال ساعات عملك المجدولة',
  'Recorded whenever the computer is in use': 'يُسجَّل كلما كان الكمبيوتر قيد الاستخدام',
  'Your login is not linked to an employee record.': 'حسابك غير مرتبط بسجل موظف.',
  'My productivity': 'إنتاجيتي',
  'Exactly what your computer reports — the same figures your manager sees.': 'ما يرسله جهازك بالضبط — نفس الأرقام التي يراها مديرك.',
  'What is recorded': 'ما الذي يُسجَّل',
  'You accepted this notice on {date}.': 'وافقت على هذا الإشعار بتاريخ {date}.',
  'I have read and accept': 'قرأت وأوافق',
  'Nothing is recorded on your computer until you accept.': 'لا يُسجَّل أي شيء على جهازك حتى توافق.',
  "Your working hours are taken from this activity (you don't clock in)": 'تُحتسب ساعات عملك من هذا النشاط (لا تسجِّل حضورك)',
  'The application in use (e.g. Revit, Excel)': 'التطبيق المستخدم (مثل Revit وExcel)',
  'Website names only (e.g. youtube.com) — not full addresses or searches': 'أسماء المواقع فقط (مثل youtube.com) — وليس العناوين الكاملة أو عمليات البحث',
  'Websites are not recorded': 'لا تُسجَّل المواقع',
  'Idle time after {n} minutes without keyboard or mouse': 'وقت الخمول بعد {n} دقيقة دون استخدام لوحة المفاتيح أو الفأرة',
  'Window titles are recorded': 'تُسجَّل عناوين النوافذ',
  'Window titles are not recorded': 'لا تُسجَّل عناوين النوافذ',
  'No screenshots, no camera, no microphone': 'لا لقطات شاشة، ولا كاميرا، ولا ميكروفون',
  'No keystrokes or typed text': 'لا ضغطات مفاتيح ولا نصوص مكتوبة',
  'Detailed records are deleted after {n} days.': 'تُحذف السجلات التفصيلية بعد {n} يومًا.',
  'Tracking is currently turned off by the company.': 'التسجيل متوقف حاليًا من قِبل الشركة.',
  'Connected computers: {list}.': 'أجهزة الكمبيوتر المرتبطة: {list}.',
  '{name} (last seen {time})': '{name} (آخر ظهور {time})',
  'none yet': 'لا يوجد بعد',
  'My days': 'أيامي',

  // Screenshots
  'Screenshots': 'لقطات الشاشة',
  'My screenshots': 'لقطات شاشتي',
  'No screenshots': 'لا لقطات شاشة',
  'No camera, no microphone': 'لا كاميرا ولا ميكروفون',
  'No screenshots in this period.': 'لا توجد لقطات شاشة في هذه الفترة.',
  'Removed': 'محذوفة',
  'blurred': 'مموّهة',
  'Screenshot from {time}': 'لقطة شاشة من {time}',
  'Screenshot — {time}': 'لقطة شاشة — {time}',
  'Delete this screenshot': 'حذف لقطة الشاشة هذه',
  'Delete this screenshot?': 'حذف لقطة الشاشة هذه؟',
  'Screenshots of the work screen, a few times an hour — you can see your own':
    'لقطات لشاشة العمل بضع مرات في الساعة — يمكنك الاطّلاع على لقطاتك',
  'Screenshots are deleted after {n} days.': 'تُحذف لقطات الشاشة بعد {n} يومًا.',
  'Deleting removes this screenshot for everyone and cannot be undone. Recorded working time is not changed.':
    'الحذف يزيل لقطة الشاشة هذه للجميع ولا يمكن التراجع عنه. ولا يتغيّر وقت العمل المسجَّل.',
  'The screenshot is removed for everyone and cannot be recovered. Recorded working time is not changed.':
    'تُزال لقطة الشاشة للجميع ولا يمكن استرجاعها. ولا يتغيّر وقت العمل المسجَّل.',
  'These are the screenshots taken on your work computer — only you and the administrators can see them. If one caught something private, tell an administrator and they can remove it.':
    'هذه هي لقطات الشاشة المأخوذة على كمبيوتر عملك — يراها أنت والمسؤولون فقط. إذا التقطت إحداها شيئًا خاصًا فأخبر أحد المسؤولين ليحذفها.',
  '{name} can see these same screenshots but cannot delete them — only an administrator can. Every time you open one is recorded in the activity log.':
    'يستطيع {name} الاطّلاع على هذه اللقطات نفسها لكن لا يمكنه حذفها — المسؤول وحده يستطيع ذلك. ويُسجَّل في سجل النشاط كل مرة تفتح فيها لقطة.',

  // Flagged ranges (review only)
  'Flagged for your review': 'موضوعة علامة لمراجعتك',
  'Regular, machine-like mouse movement (possible mouse jiggler)': 'حركة فأرة منتظمة شبيهة بالآلة (قد تكون أداة تحريك فأرة)',
  'Mouse movement only, no typing or clicking': 'حركة فأرة فقط، دون كتابة أو نقر',
  'These are only pointers for a human to check — they are never deducted from pay automatically.':
    'هذه مجرد مؤشرات ليراجعها شخص — ولا تُخصم من الراتب تلقائيًا أبدًا.',
};
